import React, { useEffect, useState, useMemo, useCallback } from "react";
import DataTable from "react-data-table-component";
import Header from "./Header";
import Footer from "./Footer";
import API_BASE_URL from "../Config";
import "./styles/SubCategory.css";
import "../styles/global.css";

/* ────────── Scroll Lock Fix Helper ────────── */
const unlockBodyScroll = () => {
  document.body.classList.remove("modal-open");
  document.body.style.removeProperty("overflow");
  document.body.style.removeProperty("padding-right");
  document.documentElement.style.removeProperty("overflow");
  document.documentElement.style.removeProperty("padding-right");
  document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
};

/* ────────── Outline SVG Icons (unchanged) ────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24"
    fill="none" stroke="currentColor"
    strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round"
  >
    {d}
  </svg>
);
const SearchIcon = (p) => (
  <Icon {...p} d={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>} />
);
const PlusIcon = (p) => (
  <Icon {...p} d={<><path d="M12 5v14M5 12h14" /></>} />
);
const GridIcon = (p) => (
  <Icon {...p} d={<>
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
  </>} />
);
const EditIcon = (p) => (
  <Icon {...p} d={<>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
  </>} />
);
const TrashIcon = (p) => (
  <Icon {...p} d={<>
    <path d="M3 6h18" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <path d="M19 6 18 20a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
  </>} />
);
const CloseIcon = (p) => (
  <Icon {...p} d={<><path d="M18 6 6 18M6 6l12 12" /></>} />
);

/* ────────── Custom Pagination ────────── */
const ChevronLeftIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
    <path d="m15 18-6-6 6-6" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const CustomPagination = ({ currentPage, totalPages, onPageChange, perPage, totalRows, onPerPageChange }) => {
  const pages = useMemo(() => {
    if (totalPages <= 1) return [1];
    const arr = [];
    const delta = 2;
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    let l;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i < right)) arr.push(i);
    }
    const withEllipsis = [];
    arr.forEach((i) => {
      if (l) {
        if (i - l === 2) withEllipsis.push(l + 1);
        else if (i - l > 2) withEllipsis.push("...");
      }
      withEllipsis.push(i);
      l = i;
    });
    return withEllipsis;
  }, [currentPage, totalPages]);

  const start = totalRows === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalRows);
  const end   = Math.min(currentPage * perPage, totalRows);

  return (
    <div className="sc-pagination">
      <div className="sc-pagination__info">
        <span>
          {totalRows === 0
            ? "No records"
            : <>Showing <b>{start}</b>–<b>{end}</b> of <b>{totalRows}</b></>
          }
        </span>
        <select
          className="sc-pagination__perpage"
          value={perPage}
          onChange={(e) => onPerPageChange(Number(e.target.value))}
        >
          {[10, 20, 50, 100].map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
      </div>
      <div className="sc-pagination__pages">
        <button
          className="sc-pagination__btn"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
        >
          <ChevronLeftIcon />
        </button>
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`e-${i}`} className="sc-pagination__ellipsis">…</span>
          ) : (
            <button
              key={p}
              className={`sc-pagination__btn ${p === currentPage ? "is-active" : ""}`}
              onClick={() => onPageChange(p)}
              aria-current={p === currentPage ? "page" : undefined}
            >
              {p}
            </button>
          )
        )}
        <button
          className="sc-pagination__btn"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
        >
          <ChevronRightIcon />
        </button>
      </div>
    </div>
  );
};

/* ────────── iPhone Toggle Switch ────────── */
const IosSwitch = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={onChange}
    className={`ios-switch ${checked ? "is-on" : "is-off"}`}
    title={checked ? "Active — click to deactivate" : "Inactive — click to activate"}
  >
    <span className="ios-switch__thumb" />
  </button>
);

/* ────────── Confirm Dialog ────────── */
const ConfirmDialog = ({ open, title, message, confirmLabel = "Confirm", confirmVariant = "danger", onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="cdlg-overlay" role="dialog" aria-modal="true" aria-labelledby="cdlg-title">
      <div className="cdlg-box">
        <div className={`cdlg-icon-wrap cdlg-icon-${confirmVariant}`}>
          {confirmVariant === "danger" ? (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
        </div>
        <h3 id="cdlg-title" className="cdlg-title">{title}</h3>
        <p className="cdlg-message">{message}</p>
        <div className="cdlg-actions">
          <button type="button" className="cdlg-btn cdlg-btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className={`cdlg-btn cdlg-btn-confirm cdlg-btn-${confirmVariant}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ────────── Component ────────── */
const SubCategory = () => {
  // ── STATE ──────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [subCategories, setSubCategories] = useState([]);
  const [filterText, setFilterText] = useState("");

  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [status, setStatus] = useState("Active");
  const [categories, setCategories] = useState([]);

  const [messagealert, setMessageAlert] = useState("");
  const [messageType, setMessageType] = useState("");

  const [isEditMode, setIsEditMode] = useState(false);
  const [editSlug, setEditSlug] = useState(null);

  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);

  // Bulk-selection state
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);

  const token = localStorage.getItem("authToken");

  // ── client-side pagination state ──────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // ── switch guard & confirm dialog ─────────────────────────────────────
  const [switchingSlug, setSwitchingSlug] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false, title: "", message: "",
    confirmLabel: "Confirm", confirmVariant: "danger", onConfirm: null,
  });

  const showConfirm = useCallback(({ title, message, confirmLabel, confirmVariant = "danger", onConfirm }) =>
    setConfirmDialog({ open: true, title, message, confirmLabel, confirmVariant, onConfirm }), []);

  const closeConfirm = useCallback(() =>
    setConfirmDialog((p) => ({ ...p, open: false, onConfirm: null })), []);

  /* ── Attach hidden.bs.modal listener ONCE to always unlock scroll ── */
  useEffect(() => {
    const modalEl = document.getElementById("addSubCategoryModal");
    if (!modalEl) return;
    const handleModalHidden = () => unlockBodyScroll();
    modalEl.addEventListener("hidden.bs.modal", handleModalHidden);
    return () => modalEl.removeEventListener("hidden.bs.modal", handleModalHidden);
  }, []);

  /* ── Image preview ── */
  useEffect(() => {
    if (!image) { setPreview(null); return; }
    const objectUrl = URL.createObjectURL(image);
    setPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [image]);

  /* ── Fetch Subcategories ── */
  const fetchSubCategories = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}api/subcategories`, {
      method: "GET",
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) {
          setSubCategories(data.data);
          console.log("[SubCategory] Fetched records:", data.data.length); // 🔍 DEBUG
        }
      })
      .catch((err) => console.error("Error fetching subcategories:", err))
      .finally(() => setLoading(false));
  }, [token]);

  /* ── Fetch Categories for dropdown ── */
  const fetchCategories = useCallback(() => {
    fetch(`${API_BASE_URL}api/categories_front`, {
      method: "GET",
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) setCategories(data.data);
      })
      .catch((err) => console.error("Error fetching categories:", err));
  }, [token]);

  useEffect(() => {
    fetchSubCategories();
    fetchCategories();
  }, [fetchSubCategories, fetchCategories]);

  /* ────────── ✅ FIXED: Derive filtered list via useMemo (no extra state, no race condition) ────────── */
  const filteredSubCategories = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return subCategories;
    return subCategories.filter((sub) =>
      sub.name?.toLowerCase().includes(q) ||
      sub.slug?.toLowerCase().includes(q) ||
      sub.status?.toLowerCase().includes(q) ||
      sub.category_title?.name?.toLowerCase().includes(q)
    );
  }, [filterText, subCategories]);

  /* ✅ Reset to page 1 ONLY when search text changes — never when data refreshes */
  useEffect(() => {
    setCurrentPage(1);
  }, [filterText]);

  /* ── Client-side pagination ── */
  const totalRows  = filteredSubCategories.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));

  /* ✅ FIX: auto-clamp currentPage when totalPages shrinks (e.g., after delete) */
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const safePage = Math.min(Math.max(1, currentPage), totalPages);

  const pagedData = useMemo(() => {
    const startIdx = (safePage - 1) * perPage;
    return filteredSubCategories.slice(startIdx, startIdx + perPage);
  }, [filteredSubCategories, safePage, perPage]);

  // 🔍 DEBUG: log pagination state every render
  console.log("[SubCategory] pagination →", {
    totalRows,
    totalPages,
    currentPage,
    safePage,
    perPage,
    pagedDataLength: pagedData.length,
  });

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handlePerPageChange = useCallback((size) => {
    setPerPage(size);
    setCurrentPage(1);
  }, []);

  const flashMessage = useCallback((msg, type = "success") => {
    setMessageAlert(msg);
    setMessageType(type);
    setTimeout(() => { setMessageAlert(""); setMessageType(""); }, 4000);
  }, []);

  /* ── Status toggle ── */
  const handleStatusChange = useCallback((slug, currentStatus) => {
    if (switchingSlug === slug) return;
    const newStatus = currentStatus === "Active" ? "InActive" : "Active";
    showConfirm({
      title: "Change Status",
      message: `Are you sure you want to set this subcategory to "${newStatus}"?`,
      confirmLabel: "Yes, Change",
      confirmVariant: "warning",
      onConfirm: () => {
        closeConfirm();
        setSwitchingSlug(slug);
        fetch(`${API_BASE_URL}api/subcategories/status/${slug}`, {
          method: "GET",
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              flashMessage(`Status updated to ${newStatus} successfully!`, "success");
              fetchSubCategories();
            } else {
              flashMessage("Failed to update status.", "danger");
            }
          })
          .finally(() => setSwitchingSlug(null));
      },
    });
  }, [switchingSlug, token, showConfirm, closeConfirm, flashMessage, fetchSubCategories]);

  /* ── Close modal helper ── */
  const closeModal = useCallback(() => {
    const modalEl = document.getElementById("addSubCategoryModal");
    if (!modalEl) return;
    const modalInstance = window.bootstrap?.Modal?.getInstance(modalEl);
    if (modalInstance) {
      modalInstance.hide();
    } else {
      unlockBodyScroll();
    }
  }, []);

  /* ── Add or Update Subcategory ── */
  const handleSaveSubCategory = useCallback((e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("name", name);
    formData.append("category_id", categoryId);
    formData.append("status", status);
    if (image) formData.append("image", image);

    const url = isEditMode
      ? `${API_BASE_URL}api/subcategories/${editSlug}`
      : `${API_BASE_URL}api/subcategories`;

    fetch(url, {
      method: "POST",
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === true) {
          flashMessage(
            isEditMode ? "Subcategory updated successfully!" : "Subcategory added successfully!",
            "success"
          );
          setName(""); setCategoryId(""); setStatus("Active");
          setImage(null); setPreview(null); setIsEditMode(false);
          fetchSubCategories();
          closeModal();
        } else {
          flashMessage("Failed to save subcategory.", "danger");
        }
      })
      .catch(() => flashMessage("Error saving subcategory!", "danger"));
  }, [name, categoryId, status, image, isEditMode, editSlug, token, flashMessage, fetchSubCategories, closeModal]);

  /* ── Edit ── */
  const handleEdit = useCallback((row) => {
    setIsEditMode(true);
    setEditSlug(row.slug);
    setName(row.name);
    setCategoryId(row.category_id);
    setStatus(row.status);
    setPreview(
      row.image
        ? `${API_BASE_URL}public/subcategory/${row.image}`
        : `${API_BASE_URL}public/noimage.jpg`
    );
    setImage(null);
    const modalEl = document.getElementById("addSubCategoryModal");
    new window.bootstrap.Modal(modalEl).show();
  }, []);

  /* ── Delete single ── */
  const handleDelete = useCallback((slug) => {
    showConfirm({
      title: "Delete Subcategory",
      message: "Are you sure you want to delete this subcategory? This action cannot be undone.",
      confirmLabel: "Yes, Delete",
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        fetch(`${API_BASE_URL}api/subcategories/destroy/${slug}`, {
          method: "GET",
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              flashMessage("Subcategory deleted successfully!", "success");
              fetchSubCategories();
            } else {
              flashMessage("Failed to delete subcategory.", "danger");
            }
          });
      },
    });
  }, [token, showConfirm, closeConfirm, flashMessage, fetchSubCategories]);

  /* ── Bulk delete ── */
  const handleBulkDelete = useCallback(() => {
    if (selectedRows.length === 0) return;
    const count = selectedRows.length;
    showConfirm({
      title: "Delete Selected",
      message: `Are you sure you want to delete ${count} selected subcategor${count > 1 ? "ies" : "y"}? This action cannot be undone.`,
      confirmLabel: `Delete ${count} Item${count > 1 ? "s" : ""}`,
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        Promise.all(
          selectedRows.map((row) =>
            fetch(`${API_BASE_URL}api/subcategories/destroy/${row.slug}`, {
              method: "GET",
              headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
            }).then((res) => res.json())
          )
        )
          .then(() => {
            flashMessage(
              `${count} subcategor${count > 1 ? "ies" : "y"} deleted successfully!`,
              "success"
            );
            setSelectedRows([]);
            setToggleCleared((p) => !p);
            fetchSubCategories();
          })
          .catch(() => flashMessage("Error deleting subcategories!", "danger"));
      },
    });
  }, [selectedRows, token, showConfirm, closeConfirm, flashMessage, fetchSubCategories]);

  /* ── Open Add modal ── */
  const openAddModal = useCallback(() => {
    setIsEditMode(false);
    setName(""); setCategoryId(""); setStatus("Active");
    setImage(null); setPreview(null);
  }, []);

  /* ── Row selection handler — stable reference ── */
  const handleSelectedRowsChange = useCallback(({ selectedRows: rows }) => {
    setSelectedRows(rows);
  }, []);

  /* ────────── Table Columns ────────── */
  const columns = useMemo(() => [
    {
      name: "ID",
      cell: (row) => <span className="sc-id-pill">#{row.id}</span>,
      width: "80px",
    },
    {
      name: "Image",
      width: "90px",
      cell: (row) => (
        <div className="sc-img-wrap">
          <img
            src={row.image ? `${API_BASE_URL}public/subcategory/${row.image}` : `${API_BASE_URL}public/noimage.jpg`}
            alt={row.name}
          />
        </div>
      ),
    },
    {
      name: "Name",
      selector: (row) => row.name,
      cell: (row) => <span className="sc-cell-name">{row.name}</span>,
      sortable: true,
      grow: 1.5,
    },
    {
      name: "Category",
      selector: (row) => row.category_title?.name ?? "",
      cell: (row) => <span className="sc-cat-pill">{row.category_title?.name ?? "—"}</span>,
      sortable: true,
    },
    {
      name: "Slug",
      selector: (row) => row.slug,
      cell: (row) => <span className="sc-slug-pill">{row.slug}</span>,
    },
    {
      name: "Status",
      cell: (row) => (
        <div className="sc-status-wrap">
          <IosSwitch
            checked={row.status === "Active"}
            onChange={() => handleStatusChange(row.slug, row.status)}
            disabled={switchingSlug === row.slug}
          />
          <span className={`sc-status-label ${row.status === "Active" ? "is-active" : "is-inactive"}`}>
            {row.status === "Active" ? "Active" : "Inactive"}
          </span>
        </div>
      ),
      width: "150px",
    },
    {
      name: "Created At",
      selector: (row) => new Date(row.created_at).toLocaleDateString("en-IN"),
      cell: (row) => (
        <span className="sc-cell-muted">
          {new Date(row.created_at).toLocaleDateString("en-IN")}
        </span>
      ),
      sortable: true,
      width: "130px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="sc-row-actions">
          <button className="sc-ico-btn sc-edit" onClick={() => handleEdit(row)} title="Edit">
            <EditIcon size={15} />
          </button>
          <button className="sc-ico-btn sc-del" onClick={() => handleDelete(row.slug)} title="Delete">
            <TrashIcon size={15} />
          </button>
        </div>
      ),
      width: "110px",
      right: true,
      ignoreRowClick: true,
    },
  ], [switchingSlug, handleStatusChange, handleEdit, handleDelete]);

  /* ────────── DataTable Custom Styles ────────── */
  const tableStyles = useMemo(() => ({
    table: { style: { background: "transparent" } },
    headRow: {
      style: {
        background: "#f0fbf6",
        borderBottom: "1px solid #d6efe2",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        fontSize: "11.5px", fontWeight: 700,
        letterSpacing: "0.07em", textTransform: "uppercase",
        color: "#5b6b66", paddingLeft: "16px", paddingRight: "16px",
      },
    },
    rows: {
      style: {
        minHeight: "72px", fontSize: "14px",
        color: "#1f2a37",
        borderBottom: "1px solid #eef4f1 !important",
        transition: "background 0.18s ease",
      },
      highlightOnHoverStyle: {
        background: "#f6fcf9",
        borderBottomColor: "#e3f0e9",
        outline: "none",
      },
      stripedStyle: { background: "#fafefb" },
    },
    cells: { style: { paddingLeft: "16px", paddingRight: "16px" } },
    pagination: { style: { display: "none" } },
  }), []);

  return (
    <>
      <div id="layout-wrapper">
        <Header />
        {loading && (
          <div className="sc-overlay"><div className="sc-spinner" /></div>
        )}

        <div className="main-content">
          <div className="page-content">
            <div className="subcat-shell">

              {/* Page header */}
              <div className="subcat-head">
                <div>
                  <h2>Subcategory</h2>
                  <p>Manage your product subcategories — add, edit, or organize them.</p>
                </div>
                <button
                  type="button" className="add-btn"
                  data-bs-toggle="modal" data-bs-target="#addSubCategoryModal"
                  onClick={openAddModal}
                >
                  <PlusIcon size={18} />
                  Add Subcategory
                </button>
              </div>

              {/* Card */}
              <div className="subcat-card">
                <div className="card-top">
                  <div className="search-box">
                    <SearchIcon size={18} />
                    <input
                      type="text"
                      placeholder="Search by name, slug, category or status…"
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                    />
                  </div>
                  <div className="total-badge">
                    <GridIcon size={16} />
                    Total:&nbsp;<b>{totalRows}</b>
                  </div>
                </div>

                {/* Alert */}
                {messagealert && (
                  <div className={`sc-alert ${messageType}`} role="alert">
                    <span>{messagealert}</span>
                    <button type="button" onClick={() => setMessageAlert("")} aria-label="Close">
                      <CloseIcon size={16} />
                    </button>
                  </div>
                )}

                {/* Bulk bar */}
                {selectedRows.length > 0 && (
                  <div className="bulk-bar">
                    <div className="count">
                      <span className="badge-num">{selectedRows.length}</span>
                      item{selectedRows.length > 1 ? "s" : ""} selected
                    </div>
                    <div className="actions">
                      <button
                        className="bulk-btn ghost"
                        onClick={() => { setSelectedRows([]); setToggleCleared((p) => !p); }}
                      >
                        Clear
                      </button>
                      <button className="bulk-btn danger" onClick={handleBulkDelete}>
                        <TrashIcon size={14} /> Delete Selected
                      </button>
                    </div>
                  </div>
                )}

                {/* Table — built-in pagination fully disabled */}
                <DataTable
                  columns={columns}
                  data={pagedData}
                  customStyles={tableStyles}
                  highlightOnHover
                  striped
                  responsive
                  selectableRows
                  onSelectedRowsChange={handleSelectedRowsChange}
                  clearSelectedRows={toggleCleared}
                  noDataComponent={
                    <div style={{ padding: "48px 20px", color: "var(--sc-muted)", textAlign: "center", fontSize: 14 }}>
                      No subcategories found.
                    </div>
                  }
                />

                {/* Custom Pagination — always render when there is data */}
                {totalRows > 0 && (
                  <CustomPagination
                    currentPage={safePage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    perPage={perPage}
                    totalRows={totalRows}
                    onPerPageChange={handlePerPageChange}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Add / Edit Modal */}
          <div
            className="modal fade" id="addSubCategoryModal"
            tabIndex="-1" aria-hidden="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <form onSubmit={handleSaveSubCategory}>
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {isEditMode ? "Edit Subcategory" : "Add Subcategory"}
                    </h5>
                    <button type="button" className="btn-close" data-bs-dismiss="modal" />
                  </div>
                  <div className="modal-body">
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="form-label">Category</label>
                        <select
                          className="form-select" value={categoryId}
                          onChange={(e) => setCategoryId(e.target.value)} required
                        >
                          <option value="">Select Category</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12">
                        <label className="form-label">Subcategory Name</label>
                        <input
                          type="text" className="form-control"
                          placeholder="Enter subcategory name"
                          value={name} onChange={(e) => setName(e.target.value)} required
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Image</label>
                        <input
                          type="file" className="form-control" accept="image/*"
                          onChange={(e) => setImage(e.target.files[0])}
                        />
                        {preview && (
                          <img src={preview} alt="Preview" className="preview-thumb" />
                        )}
                      </div>
                      <div className="col-12 col-sm-6">
                        <label className="form-label">Status</label>
                        <select
                          className="form-select" value={status}
                          onChange={(e) => setStatus(e.target.value)}
                        >
                          <option value="Active">Active</option>
                          <option value="InActive">Inactive</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-light" data-bs-dismiss="modal">
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      {isEditMode ? "Update Subcategory" : "Add Subcategory"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Custom Confirm Dialog */}
          <ConfirmDialog
            open={confirmDialog.open}
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmLabel={confirmDialog.confirmLabel}
            confirmVariant={confirmDialog.confirmVariant}
            onConfirm={confirmDialog.onConfirm}
            onCancel={closeConfirm}
          />

          <Footer />
        </div>
      </div>
    </>
  );
};

export default SubCategory;